/**
 * Minting printed codes (design doc 5.1, work package 2).
 *
 * A code is eight characters drawn uniformly from the thirty-character
 * alphabet: about 650 billion of them. That number is the whole defence
 * against somebody sitting in the bar typing guesses, so the draw has to be
 * genuinely uniform and the batch has to be genuinely free of duplicates.
 * Two cans carrying the same code is a refund and a support ticket.
 */
import { CODE_ALPHABET, CODE_LENGTH } from "./code-format";

/** Fills the array with random bytes and returns it. `crypto.getRandomValues` in production. */
export type RandomBytes = (out: Uint8Array) => Uint8Array;

/**
 * The largest multiple of the alphabet that fits in a byte. Bytes at or above
 * this are thrown away rather than folded back round, because folding would
 * make the first sixteen letters likelier than the rest and quietly shrink
 * the code space.
 */
const CEILING = 256 - (256 % CODE_ALPHABET.length);

/** How many draws to allow before deciding the randomness is broken. */
const PATIENCE = 64;

export function randomCode(random: RandomBytes): string {
  let out = "";
  const buffer = new Uint8Array(CODE_LENGTH);
  let cursor = buffer.length;
  let draws = 0;
  while (out.length < CODE_LENGTH) {
    if (cursor >= buffer.length) {
      if (draws >= PATIENCE) throw new Error("randomCode: not enough usable random bytes");
      random(buffer);
      draws += 1;
      cursor = 0;
    }
    const byte = buffer[cursor] as number;
    cursor += 1;
    if (byte >= CEILING) continue;
    out += CODE_ALPHABET[byte % CODE_ALPHABET.length];
  }
  return out;
}

/**
 * A batch. Codes are unique within it; the database's primary key is what
 * makes them unique across every batch ever printed, and a collision there
 * is a retry, not a crash.
 */
export function mintCodes(count: number, random: RandomBytes): string[] {
  if (!Number.isInteger(count) || count < 1) {
    throw new Error(`mintCodes: count must be a positive whole number, got ${count}`);
  }
  const codes = new Set<string>();
  let attempts = 0;
  const ceiling = count * PATIENCE;
  while (codes.size < count) {
    if (attempts >= ceiling) {
      throw new Error(
        `mintCodes: could not mint ${count} different codes; the source of randomness is repeating itself`,
      );
    }
    attempts += 1;
    codes.add(randomCode(random));
  }
  return [...codes];
}
